// SPDX-License-Identifier: Apache-2.0
// File: src/audio/WebMidiController.ts
import type { SynthParamId } from '../ui/theme/PocketGridRetroTheme';

export interface MidiDeviceStatus {
  supported: boolean;
  connectedInputs: string[];
  lastEventText: string;
  lastEventTimeMs: number;
}

export interface MidiCallbacks {
  onNoteOn: (midiNote: number, velocityNorm: number, channel: number) => void;
  onNoteOff: (midiNote: number, channel: number) => void;
  onDrumPadNote: (drumIdx: number, velocityNorm: number) => void;
  onParamCcChange: (paramId: string, normalizedValue: number, ccNumber: number) => void;
  onPitchBend: (semitones: number) => void;
  onSustainPedal: (held: boolean) => void;
  onTransportStartStop: (playing: boolean) => void;
  onCcLearned: (paramId: string, ccNumber: number) => void;
  onStatusChange: (status: MidiDeviceStatus) => void;
}

const MIDI_CC_STORAGE_KEY = 'pocketgrid_midi_cc_mappings_v1';

// Default Param -> Primary MIDI CC badge shown on sliders (Zero collisions with CC 102-117 for Deep Menu)
export const DEFAULT_PARAM_TO_CC: Record<string, number> = {
  // Top 16 Performance Layout
  cutoff: 74,
  resonance: 71,
  drive: 20,
  waveShape: 14,
  ampAttack: 73,
  ampDecay: 75,
  ampSustain: 79,
  ampRelease: 72,
  fltEnvAmount: 77,
  fltDecay: 78,
  lfoRate: 76,
  lfoDepth: 1,
  oscBalance: 15,
  detune: 94,
  delayMix: 93,
  reverbSpace: 91,

  // Top 32 Extended Matrix
  osc1Wave: 14,
  osc1Octave: 40,
  osc2Wave: 41,
  osc2Octave: 42,
  osc2Interval: 43,
  osc2Detune: 44,
  pulseWidth: 45,
  unisonDetune: 94,
  osc1Level: 46,
  osc2Level: 47,
  subLevel: 48,
  noiseLevel: 49,
  filterDrive: 20,
  keyTracking: 50,
  fltEnvDepth: 77,
  fltAttack: 51,
  fltSustain: 52,
  fltRelease: 53,
  lfo1Rate: 76,
  lfo1Depth: 1,
  lfo2Rate: 54,
  lfo2ToPwm: 55,
  portamento: 5,
  delayTimeMix: 93,

  // Deep Menu: Character & Physical Resonators (Clean CC 102–117)
  algMorph: 102,
  waveFold: 103,
  fmFeedback: 104,
  formantShift: 105,
  vowelMorphX: 106,
  vowelMorphY: 107,
  modAttack: 108,
  modDecay: 109,
  carAttack: 110,
  carDecay: 111,
  chorusDelayMix: 112,
  spaceReverbMix: 113,
  resonatorDecay: 114,
  drumDispersion: 115,
  wowFlutter: 116,
  limiterDrive: 117,

  // Live Performance FX Sliders
  perfDjFilter: 60,
  perfStutter: 61,
  perfRevDelay: 62,
  perfBeatGate: 63,
  perfPunchComp: 64,
  perfPitchBend: 65,
  perfTapeStop: 66,
};

export function loadParamToCcMappings(): Record<string, number> {
  try {
    const raw = localStorage.getItem(MIDI_CC_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PARAM_TO_CC };
    const parsed = JSON.parse(raw) as Record<string, number>;
    return { ...DEFAULT_PARAM_TO_CC, ...parsed };
  } catch {
    return { ...DEFAULT_PARAM_TO_CC };
  }
}

export function saveParamToCcMappings(map: Record<string, number>): void {
  try {
    localStorage.setItem(MIDI_CC_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Ignore storage quota errors
  }
}

export class WebMidiController {
  private midiAccess: MIDIAccess | null = null;
  private callbacks: MidiCallbacks | null = null;
  private paramToCc: Record<string, number> = loadParamToCcMappings();
  private learningParamId: string | null = null;
  private lastTouchedParamId: string = 'cutoff';
  private globalLearnMode = false;
  private status: MidiDeviceStatus = {
    supported: typeof navigator !== 'undefined' && 'requestMIDIAccess' in navigator,
    connectedInputs: [],
    lastEventText: 'No MIDI activity yet',
    lastEventTimeMs: 0,
  };

  async initialize(callbacks: MidiCallbacks): Promise<MidiDeviceStatus> {
    this.callbacks = callbacks;
    this.paramToCc = loadParamToCcMappings();

    if (typeof navigator === 'undefined' || !('requestMIDIAccess' in navigator)) {
      this.status = {
        supported: false,
        connectedInputs: [],
        lastEventText: 'Web MIDI not supported in this browser (use Chrome/Edge/Brave/Android)',
        lastEventTimeMs: Date.now(),
      };
      this.callbacks.onStatusChange(this.status);
      return this.status;
    }

    try {
      try {
        this.midiAccess = await navigator.requestMIDIAccess({ sysex: true });
      } catch {
        this.midiAccess = await navigator.requestMIDIAccess({ sysex: false });
      }

      this.bindInputs();
      this.midiAccess.onstatechange = () => {
        this.bindInputs();
      };
    } catch {
      this.status = {
        supported: true,
        connectedInputs: [],
        lastEventText: 'MIDI permission denied or unavailable',
        lastEventTimeMs: Date.now(),
      };
      this.callbacks.onStatusChange(this.status);
    }

    return this.status;
  }

  private bindInputs(): void {
    if (!this.midiAccess) return;
    const names: string[] = [];

    this.midiAccess.inputs.forEach((input) => {
      if (input.state === 'connected') {
        names.push(input.name || `MIDI Input ${input.id}`);
        input.onmidimessage = (e) => this.handleMidiMessage(input.name || 'MIDI', e);
      }
    });

    this.status = {
      ...this.status,
      supported: true,
      connectedInputs: names,
      lastEventText:
        names.length > 0
          ? `Connected: ${names.join(', ')}`
          : 'Plug in any USB/BLE MIDI Controller',
    };
    this.callbacks?.onStatusChange(this.status);
  }

  // Record whichever slider/setting the user just touched or adjusted
  notifyParameterTouched(paramId: string): void {
    this.lastTouchedParamId = paramId;
    if (this.globalLearnMode) {
      this.learningParamId = paramId;
    }
  }

  getLastTouchedParam(): string {
    return this.lastTouchedParamId;
  }

  setGlobalMidiLearnMode(enabled: boolean): string | null {
    this.globalLearnMode = enabled;
    this.learningParamId = enabled ? this.lastTouchedParamId : null;
    return this.learningParamId;
  }

  isGlobalMidiLearnMode(): boolean {
    return this.globalLearnMode;
  }

  setLearningParam(paramId: string | null): void {
    this.learningParamId = paramId;
    if (paramId) {
      this.lastTouchedParamId = paramId;
    } else {
      this.globalLearnMode = false;
    }
  }

  getLearningParam(): string | null {
    return this.learningParamId;
  }

  getParamToCcMap(): Record<string, number> {
    return { ...this.paramToCc };
  }

  resetDefaultMappings(): Record<string, number> {
    this.paramToCc = { ...DEFAULT_PARAM_TO_CC };
    saveParamToCcMappings(this.paramToCc);
    return { ...this.paramToCc };
  }

  private updateActivity(text: string): void {
    this.status = {
      ...this.status,
      lastEventText: text,
      lastEventTimeMs: Date.now(),
    };
    this.callbacks?.onStatusChange(this.status);
  }

  private handleMidiMessage(_deviceName: string, event: MIDIMessageEvent): void {
    const data = event.data;
    if (!data || data.length === 0) return;

    const statusByte = data[0];

    if (statusByte === 0xfa || statusByte === 0xfb) {
      this.updateActivity('MIDI START');
      this.callbacks?.onTransportStartStop(true);
      return;
    }
    if (statusByte === 0xfc) {
      this.updateActivity('MIDI STOP');
      this.callbacks?.onTransportStartStop(false);
      return;
    }

    const command = statusByte & 0xf0;
    const channel = statusByte & 0x0f;
    const d1 = data.length > 1 ? data[1] & 0x7f : 0;
    const d2 = data.length > 2 ? data[2] & 0x7f : 0;

    // 1. NOTE ON
    if (command === 0x90 && d2 > 0) {
      const velNorm = Math.max(0.15, Math.min(1.0, d2 / 127.0));
      this.updateActivity(`NOTE ON ${d1} (Vel ${d2})`);

      if (channel === 9) {
        const drumIdx = Math.abs(d1 - 36) % 6;
        this.callbacks?.onDrumPadNote(drumIdx, velNorm);
      } else {
        this.callbacks?.onNoteOn(d1, velNorm, channel);
      }
      return;
    }

    // 2. NOTE OFF
    if (command === 0x80 || (command === 0x90 && d2 === 0)) {
      this.updateActivity(`NOTE OFF ${d1}`);
      if (channel !== 9) {
        this.callbacks?.onNoteOff(d1, channel);
      }
      return;
    }

    // 3. CONTROL CHANGE (0xB0) — Knobs, Faders, Mod Wheel, Sustain Pedal
    if (command === 0xb0) {
      const ccNum = d1;
      const ccVal = d2;
      const norm = ccVal / 127.0;

      // Sustain Pedal (CC 64) when not learning
      if (ccNum === 64 && !this.learningParamId && !this.globalLearnMode) {
        const held = ccVal >= 64;
        this.updateActivity(`SUSTAIN PEDAL ${held ? 'ON' : 'OFF'}`);
        this.callbacks?.onSustainPedal(held);
        return;
      }

      // DAW-Style Last-Touched MIDI Learn!
      const targetLearnParam =
        this.learningParamId || (this.globalLearnMode ? this.lastTouchedParamId : null);

      if (targetLearnParam) {
        // Remove this CC# from any other param if already assigned so 1 knob maps cleanly to the last-touched slider
        Object.keys(this.paramToCc).forEach((pid) => {
          if (this.paramToCc[pid] === ccNum && pid !== targetLearnParam) {
            this.paramToCc[pid] = -1;
          }
        });
        this.paramToCc[targetLearnParam] = ccNum;
        saveParamToCcMappings(this.paramToCc);

        if (!this.globalLearnMode) {
          this.learningParamId = null;
        }
        this.updateActivity(`LEARNED CC ${ccNum} -> ${targetLearnParam}`);
        this.callbacks?.onCcLearned(targetLearnParam, ccNum);
        this.callbacks?.onParamCcChange(targetLearnParam, norm, ccNum);
        return;
      }

      // Lookup any parameter mapped to this CC#
      const matchedParams = Object.keys(this.paramToCc).filter(
        (pid) => this.paramToCc[pid] === ccNum
      );

      if (matchedParams.length === 0 && ccNum >= 20 && ccNum <= 27) {
        const fallbackOrder: SynthParamId[] = [
          'algMorph',
          'waveFold',
          'fmFeedback',
          'resonatorDecay',
          'formantShift',
          'vowelMorphX',
          'chorusDelayMix',
          'spaceReverbMix',
        ];
        const fallbackParam = fallbackOrder[ccNum - 20];
        if (fallbackParam) {
          this.updateActivity(`CC ${ccNum} (${ccVal})`);
          this.callbacks?.onParamCcChange(fallbackParam, norm, ccNum);
          return;
        }
      }

      if (matchedParams.length > 0) {
        this.updateActivity(`CC ${ccNum} (${ccVal})`);
        for (const pid of matchedParams) {
          this.callbacks?.onParamCcChange(pid, norm, ccNum);
        }
      } else {
        this.updateActivity(`CC ${ccNum} (${ccVal}) [Move any slider + click MIDI LEARN]`);
      }
      return;
    }

    // 4. PITCH BEND WHEEL (0xE0)
    if (command === 0xe0) {
      const bend14 = (d2 << 7) | d1;
      const normBend = (bend14 - 8192) / 8192.0;
      const semitones = normBend * 2.0;
      this.updateActivity(`PITCH BEND ${semitones >= 0 ? '+' : ''}${semitones.toFixed(1)} st`);
      this.callbacks?.onPitchBend(semitones);
    }
  }
}

export const webMidiController = new WebMidiController();
